import { FC, MouseEvent, PropsWithChildren, useEffect, useRef } from "react";
import { Button, MobileStepper } from "@mui/material";
import { useFormContext } from "react-hook-form";
import { AllFieldsRequired } from "components/NewOrders/AddGlobalAttributes/addGlobalAttributes.styles.ts";
import {
	FixCatalogGlobalAttributesDescription,
	FixCatalogGlobalAttributesLoadingText,
	FixCatalogGlobalAttributesNextGroupText,
	FixCatalogGlobalAttributesSubtitle,
	FixCatalogGlobalAttributesTitle,
	FixCatalogGlobalAttributesUpdateGroupsText,
	NewOrderBack,
	NewOrderContinueButton
} from "components/NewOrders/constants.ts";
import { AllFieldsRequiredText, FileNameText } from "constants/text.ts";
import {
	CatalogContainer,
	CatalogContainerText,
	CatalogContainerTitle,
	CatalogsContainer,
	Content,
	CurrentCatalog,
	Description,
	DropdownContainer,
	Header,
	LoadingContainer,
	LoadingText,
	StyledDialog,
	SubTitle
} from "./FixGlobalAttributesDialog.styles";
import { DialogTitle } from "components/Dialog";
import { GlobalAttributeFields } from "../AddGlobalAttributes/forms/schema";
import LinearProgress from "@mui/material/LinearProgress";

interface FixGlobalAttributesDialogProps extends PropsWithChildren {
	handleDialogClose: () => void;
	dialogState: boolean;
	handleStepperForward: (event: MouseEvent<HTMLButtonElement>) => void;
	handleStepperBack: (event: MouseEvent<HTMLButtonElement>) => void;
	step: number;
	maxSteps: number;
	onSave: (values: GlobalAttributeFields) => void;
	currentCatalogName: string;
	catalogsCount: number;
	currentCatalog: number;
	csvFileName: string;
	isLoading?: boolean;
}

const FixGlobalAttributesDialog: FC<FixGlobalAttributesDialogProps> = ({
	handleDialogClose,
	dialogState,
	handleStepperForward,
	handleStepperBack,
	step,
	maxSteps,
	onSave,
	children,
	currentCatalogName,
	catalogsCount,
	currentCatalog,
	csvFileName,
	isLoading
}) => {
	const { handleSubmit } = useFormContext<GlobalAttributeFields>();
	const lastCatalogNameRef = useRef(currentCatalogName);
	const lastCsvFileNameRef = useRef(csvFileName);
	const lastCatalogRef = useRef(currentCatalog);
	const lastCatalogsCountRef = useRef(catalogsCount);

	useEffect(() => {
		if (currentCatalogName) lastCatalogNameRef.current = currentCatalogName;
		if (csvFileName) lastCsvFileNameRef.current = csvFileName;
		if (currentCatalog > 0) lastCatalogRef.current = currentCatalog;
		if (catalogsCount > 0) lastCatalogsCountRef.current = catalogsCount;
	}, [currentCatalogName, csvFileName, currentCatalog, catalogsCount]);

	return (
		<StyledDialog
			open={dialogState}
			data-testid="leave-flow-dialog"
			onClose={handleDialogClose}
			hideBackdrop
		>
			<form
				onSubmit={(event) => {
					event.stopPropagation();
					handleSubmit(onSave)(event);
				}}
				noValidate
			>
				<Header>
					<DialogTitle
						title={FixCatalogGlobalAttributesTitle}
						handleClose={handleDialogClose}
					/>
					<AllFieldsRequired style={{ paddingLeft: 24 }}>{AllFieldsRequiredText}</AllFieldsRequired>
				</Header>
				<Content>
					<SubTitle>{FixCatalogGlobalAttributesSubtitle}</SubTitle>
					<Description>{FixCatalogGlobalAttributesDescription}</Description>
					<CatalogsContainer>
						<CurrentCatalog>
							<CatalogContainer>
								<CatalogContainerTitle>
									{(() => {
										const displayCatalogsCount =
											catalogsCount > 0 ? catalogsCount : lastCatalogsCountRef.current;
										const displayCurrentCatalog =
											currentCatalog > 0 ? currentCatalog : lastCatalogRef.current;
										return displayCatalogsCount > 0
											? `Catalog ${displayCurrentCatalog} of ${displayCatalogsCount}`
											: "Catalog";
									})()}
								</CatalogContainerTitle>
								<CatalogContainerText data-testid="catalogName">
									{currentCatalogName || lastCatalogNameRef.current}
								</CatalogContainerText>
							</CatalogContainer>
							<CatalogContainer>
								<CatalogContainerTitle>{FileNameText}</CatalogContainerTitle>
								<CatalogContainerText data-testid="imported-csv-filename">
									{csvFileName || lastCsvFileNameRef.current}
								</CatalogContainerText>
							</CatalogContainer>
						</CurrentCatalog>
						<DropdownContainer>
							{isLoading ? (
								<LoadingContainer>
									<LoadingText>{FixCatalogGlobalAttributesLoadingText}</LoadingText>
									<LinearProgress sx={{ width: "80%", minWidth: 200, maxWidth: 472 }} />
								</LoadingContainer>
							) : (
								<>
									{children}
									<MobileStepper
										variant="text"
										steps={maxSteps}
										position="static"
										activeStep={step}
										nextButton={
											step !== maxSteps - 1 ? (
												<Button
													size="small"
													variant="contained"
													onClick={handleStepperForward}
													data-testid="global-attribute-modal-save-button"
												>
													{NewOrderContinueButton}
												</Button>
											) : (
												<Button
													data-testid="global-attribute-modal-save-button"
													size="small"
													variant="contained"
													type="submit"
												>
													{currentCatalog === catalogsCount
														? FixCatalogGlobalAttributesUpdateGroupsText
														: FixCatalogGlobalAttributesNextGroupText}
												</Button>
											)
										}
										backButton={
											<Button
												data-testid="global-attribute-modal-back-button"
												onClick={handleStepperBack}
												style={{ fontSize: 16 }}
												disabled={step === 0}
												sx={step === 0 ? { visibility: "hidden" } : undefined}
												variant="text"
											>
												{NewOrderBack}
											</Button>
										}
									/>
								</>
							)}
						</DropdownContainer>
					</CatalogsContainer>
				</Content>
			</form>
		</StyledDialog>
	);
};

export default FixGlobalAttributesDialog;
